import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Space, Table, Button, Input, Modal, Spin, QRCode } from "antd";
import { ProfileOutlined } from "@ant-design/icons";
import type { ColumnsType, ColumnType } from "antd/es/table";
import type { InputRef } from "antd";
import type { FilterConfirmProps } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { cloneDeep } from "lodash";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { activeUser } from "../../services/AuthServices";
import { Registration, RegistrationStatus } from "../../models/Registration";
import { ParticipantDto } from "../../models/Participant";
import { Notification, NotificationType } from "../../models/Notification";
import { PdfPreview } from "../../components/PdfPreview/PdfPreview";
import { getRegistrationListReportTemplate } from "../../document-templates/RegistrationListReportTemplate";
import {
  getAllRegistrations,
  updateRegistration,
  payRegistration,
  deleteRegistration,
  updateRegistrationComment,
  getCompanyRegistration,
  getRegistration,
} from "../../services/RegistrationService";
import { createParticipant } from "../../services/ParticipantService";
import { getAllNotifications } from "../../services/NotificationService";
import { getAllEvents } from "../../services/EventService";
import { formatDate, formatTime } from "../../core/Utility";
import "./RegistrationListPage.scss";
import { RoleType } from "../../models/Role";
import { EventDto } from "../../models/Event";
import { Toast } from "primereact/toast";
import { AxiosResponse } from "axios";
import CustomButton from "../../components/AccommodationData/CustomButton/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage } from "../../store-redux/slices/ChangePageSlice";
import SettingsColumns from "../../components/SettingsColumns/SettingsColumns";
import { setFiltersRegistration } from "../../store-redux/slices/FiltersRegistrationSlice";
import { setPageItems } from "../../store-redux/slices/ChangeItemsPerPage";

const RegistrationListPage = () => {
  const navigate = useNavigate();
  const { setBreadcrumb, setNotificationNumber } = useApplicationContext();

  const [registrationList, setRegistrationList] = useState<Registration[]>([]);
  const [filteredRegistrationList, setFilteredRegistrationList] = useState<
    Registration[]
  >([]);
  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const [selectedRegistration, setSelectedRegistration] =
    useState<Registration>();
  const [newStatus, setNewStatus] = useState<RegistrationStatus>(
    RegistrationStatus.Pending
  );
  const [cancelledStatus, setCancelledStatus] = useState(false);
  const searchInput = useRef<InputRef>(null);
  const [selectedModalItemId, setSelectedModalItemId] = useState<number>(0);
  const [activeSpinner, setActiveSpinner] = useState<boolean>(false);
  const [allEventOptions, setAllEventOptions] = useState<
    { text: string; value: string }[]
  >([]);
  const [openExportButtons, setOpenExportButtons] = useState<boolean>(false);
  type DataIndex = keyof Registration;

  const [pdfData, setPdfData] = useState<any>(
    getRegistrationListReportTemplate(filteredRegistrationList)
  );
  const [resetData, setResetData] = useState<boolean>(false);

  const toast = useRef<any>(null);
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);
  const [commentId, setCommentId] = useState("");
  const [regsitrationComment, setRegistrationComment] = useState("");
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const { TextArea } = Input;
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [paginationNumber, setPaginationNumber] = useState<any>(30);
  const [roomTypeOptions, setRoomTypeOptions] = useState();
  const [roomTypeKindOptions, setRoomKindOptions] = useState();

  const typeOfParticipationOptions = [
    // {
    //   text: "Svečana večera",
    //   value: "Svečana večera",
    // },
    {
      text: "Kotizacija",
      value: "Kotizacija",
    },
    // {
    //   text: "Svečana večera + kotizacija",
    //   value: "Svečana večera + kotizacija",
    // },
    {
      text: "Učešće sa smeštajem",
      value: "Učešće sa smeštajem",
    },
  ];

  const typeOptions = [
    { text: "Predavač", value: "Predavač" },
    { text: "Učesnik", value: "Učesnik" },
    { text: "Gost", value: "Gost" },
    { text: "VIP", value: "VIP" },
    { text: "Sponzorisan", value: "Sponzorisan" },
  ];

  const statusOptions = [
    { text: "Uplaćeno", value: RegistrationStatus.Paid },
    { text: "Rezervisano", value: RegistrationStatus.Reserved },
    { text: "Odobreno", value: RegistrationStatus.Approved },
    { text: "Odbijeno", value: RegistrationStatus.Rejected },
    { text: "Na čekanju", value: RegistrationStatus.Pending },
  ];

  ////// COLUMNS VISABLE

  const [listActiveColumns, setListActiveColumns] = useState([
    { text: "Red. broj", active: true },
    { text: "Status", active: true },
    { text: "Komentar", active: true },
    { text: "Ime", active: true },
    { text: "Prezime", active: true },
    { text: "Tip učešća", active: true },
    { text: "Pravno lice", active: true },
    { text: "Datum prijave", active: true },
    { text: "Tip smeštaja", active: true },
    { text: "Tip sobe", active: true },
    { text: "Događaj", active: true },
    { text: "Promena statusa", active: true },
    { text: "Profaktura", active: true },
    { text: "Tip kreiranja", active: true },
    { text: "Administrator", active: true },
    { text: "Opcije", active: true },
  ]);

  useEffect(() => {
    const jsonListActiveColumnsA = localStorage.getItem(
      "activeColumnsRegistration"
    );

    if (!jsonListActiveColumnsA) {
      localStorage.setItem(
        "activeColumnsRegistration",
        JSON.stringify(listActiveColumns)
      );
    } else {
      const jsonArray = JSON.parse(jsonListActiveColumnsA);
      if (
        jsonArray &&
        JSON.stringify(jsonArray) !== JSON.stringify(listActiveColumns)
      ) {
        setListActiveColumns(jsonArray);
      }
    }
  }, []);

  ///// IMPLEMENTATION REDUX
  const dispatch = useDispatch();
  const currentPage = useSelector((state: any) => state.changePage.currentPage);
  const itemsPerPage = useSelector(
    (state: any) => state.changeItemsPerPage.currentItemsPage
  );

  const filtersRegistrationState = useSelector(
    (state: any) => state.filterRegistration.filters
  );

  ///// END IMPLEMENTATION REDUX

  useEffect(() => {
    setActiveSpinner(true);
    setBreadcrumb(["Prijave", "Lista prijava"]);
    registrationData();
    eventData();
    fetchAllNotifications();
  }, []);

  const fetchAllNotifications = () => {
    setActiveSpinner(true);
    let copyOfNewNotifications: Notification[] = [];
    getAllNotifications()
      .then((response) => {
        //ts-ignore
        response.data.result.map((item: any) => {
          if (
            activeUser?.role.name === RoleType.Agency &&
            item.type === NotificationType.AGENCY
          ) {
            copyOfNewNotifications.push(item);
          }
          if (
            activeUser?.role.name === RoleType.Employee &&
            item.type === NotificationType.EMPLOYEE
          ) {
            copyOfNewNotifications.push(item);
          }
          if (activeUser?.role.name === RoleType.Admin) {
            copyOfNewNotifications.push(item);
          }
        });
        const unseenNotifications = copyOfNewNotifications.filter(
          (oneNotification: Notification) => oneNotification.seen === false
        );
        setNotificationNumber(unseenNotifications.length);
      })
      //.finally(() => setActiveSpinner(false))
      .catch((err) => console.log(err));
  };

  const handleComment = (e: any) => {
    setCommentText(e.target.value);
  };

  ////
  const postNewComment = () => {
    updateRegistrationComment(commentId, { note: commentText }).then(
      (res: AxiosResponse) => {
        registrationData();
      }
    );
  };

  const registrationData = () => {
    setActiveSpinner(true);
    getAllRegistrations()
      .then((response) => {
        ////// GET UNIQUE ROOM TYPE
        const resultR = response.data.result;
        const uniqueRoomTypes: any = [];
        const uniqueObjectsArray: any = [];

        resultR.forEach((el: any) => {
          if (el && el.eventXRoomType && el.eventXRoomType.roomType) {
            const roomType = el.eventXRoomType.roomType;

            if (!uniqueRoomTypes.includes(roomType)) {
              uniqueRoomTypes.push(roomType);
            }

            uniqueRoomTypes.forEach((obj: any) => {
              const index = uniqueObjectsArray.findIndex(
                (uniqueObj: any) => uniqueObj.id === obj.id
              );

              if (index === -1) {
                uniqueObjectsArray.push(obj);
              }
            });
          }
        });

        let dataRoomType = uniqueObjectsArray?.map((el: any) => {
          return { text: el.name, value: el.name };
        });

        setRoomTypeOptions(dataRoomType);

        ////// END GET UNIQUE ROOM TYPE

        ////// GET UNIQUE ROOM KIND
        const uniqueRoomKinds: any = [];
        const uniqueObjectsArrayKinds: any = [];

        resultR.forEach((el: any) => {
          if (el && el.participant && el.participant.roomKind) {
            const roomKind = el.participant.roomKind;

            if (!uniqueRoomKinds.includes(roomKind)) {
              uniqueRoomKinds.push(roomKind);
            }

            uniqueRoomKinds.forEach((obj: any) => {
              const index = uniqueObjectsArrayKinds.findIndex(
                (uniqueObj: any) => uniqueObj.id === obj.id
              );

              if (index === -1) {
                uniqueObjectsArrayKinds.push(obj);
              }
            });
          }
        });

        let dataRoomKind = uniqueObjectsArrayKinds?.map((el: any) => {
          return { text: el.name, value: el.name };
        });

        setRoomKindOptions(dataRoomKind);

        ////// END GET UNIQUE ROOM KIND

        const registrationArray: Registration[] = response.data.result.map(
          (el: Registration | any) => {
            const typeOP =
              el.dinner && el.registrationFee
                ? `Svečana večera + kotizacija`
                : el.dinner
                ? `Svečana večera`
                : el.registrationFee
                ? `Kotizacija`
                : "Učešće sa smeštajem";

            return {
              ...el,
              companyName:
                el.registrationCompanyId === null
                  ? "/"
                  : `${el?.registrationCompanyName?.slice(0, 35)} ${
                      el?.registrationCompanyName.length > 35 ? "..." : ""
                    }`,
              userName: el.user === null ? "/" : el?.user?.nameAndSurname,
              typeOfParticipation: typeOP,
            };
          }
        );
        setRegistrationList(registrationArray);
        setFilteredRegistrationList(registrationArray);
        setActiveSpinner(false);
        setPageNumber(currentPage); /// - SET NUMBER PAGE FROM STORE
      })
      .catch((err) => console.log(err));
  };

  const eventData = () => {
    getAllEvents()
      .then((eventResponse) => {
        let newEventOptions: { text: string; value: string }[] = [];
        eventResponse?.data?.result?.map((eventItem: EventDto) => {
          newEventOptions.push({
            text: eventItem.name,
            value: eventItem.name,
          });
        });
        setAllEventOptions(newEventOptions);
      })
      .catch((err) => console.log(err));
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<Registration> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži po imenu`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handleSearch(selectedKeys as string[], confirm, dataIndex);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, border: "none" }}
          >
            Pretraži
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              close();
              setSearchedColumn("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Poništi
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#C7363D" : undefined }} />
    ),
    onFilter: (
      value,
      record //@ts-ignore
    ) =>
      (record[dataIndex] ?? "")
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
            color: "#0D4077",
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<Registration | any> = [
    {
      title: "Red. broj",
      width: 70,
      dataIndex: "firstName",
      key: "firstName",
      render: (firstName, text, index) => {
        return (
          <span className="blue-text">
            {(pageNumber - 1) * itemsPerPage + index + 1}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      // sorter: (a, b) => ((a.status ?? "") < (b.status ?? "") ? -1 : 1),
      filters: statusOptions,
      filteredValue: filtersRegistrationState?.filters?.status,
      onFilter: (value: any, record: any) => record?.status === value,
      render: (status, record) => (
        <div
          id={`myqrcode-${record.id}`}
          className="status-container"
          onClick={() => {
            if (activeUser?.role.name !== RoleType.Test) {
              handleChangeStatus(record);
            }
          }}
        >
          <span
            className={
              status === RegistrationStatus.Pending
                ? "status-dot status-dot-pending"
                : status === RegistrationStatus.Rejected
                ? "status-dot status-dot-rejected"
                : status === RegistrationStatus.Approved
                ? "status-dot status-dot-approved"
                : status === RegistrationStatus.Reserved
                ? "status-dot status-dot-reserved"
                : status === RegistrationStatus.Paid
                ? "status-dot status-dot-paid"
                : ""
            }
          >
            _
          </span>
          <span className="status-text">{status}</span>
          <QRCode
            style={{ display: "none" }}
            size={800}
            value={
              (record?.id as string) +
                ";" +
                record.phone +
                ";" +
                record.firstName +
                ";" +
                record.lastName +
                ";" +
                record?.event?.name || "-"
            }
            bgColor="#fff"
          />
        </div>
      ),
    },
    {
      title: "Komentar",
      width: 200,
      dataIndex: "note",
      key: "note",
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "note" &&
        filtersRegistrationState?.sort?.order,
      sorter: (a, b) => ((a?.note ?? "") < (b?.note ?? "") ? -1 : 1),
      render: (note, data) => (
        <div
          onClick={() => {
            setCommentText(data.note);
            setCommentId(data.id);
            setRegistrationComment(`${data.firstName} ${data.lastName}`);
            setShowCommentModal(true);
          }}
          className="comment-field"
        >
          <span className="blue-text">
            {note && note.length >= 35
              ? note.slice(0, 35) + "..."
              : note ?? "/"}
          </span>
        </div>
      ),
    },
    {
      title: "Ime",
      dataIndex: "firstName",
      key: "firstName",
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "firstName" &&
        filtersRegistrationState?.sort?.order,
      filteredValue: filtersRegistrationState?.filters?.firstName,
      sorter: (a, b) => (a.firstName < b.firstName ? -1 : 1),
      render: (firstName) => <span className="blue-text">{firstName}</span>,
      ...getColumnSearchProps("firstName"),
    },
    {
      title: "Prezime",
      dataIndex: "lastName",
      key: "lastName",
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "lastName" &&
        filtersRegistrationState?.sort?.order,
      filteredValue: filtersRegistrationState?.filters?.lastName,
      sorter: (a, b) => (a.lastName < b.lastName ? -1 : 1),
      render: (lastName) => <span className="blue-text">{lastName}</span>,
      ...getColumnSearchProps("lastName"),
    },
    {
      title: "Tip učešća",
      dataIndex: "typeOfParticipation",
      key: "typeOfParticipation",
      filteredValue: filtersRegistrationState?.filters?.typeOfParticipation,
      sorter: (a, b) =>
        a.typeOfParticipation < b.typeOfParticipation ? -1 : 1,
      render: (typeOfParticipation) => (
        <span className="blue-text">{typeOfParticipation}</span>
      ),
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "typeOfParticipation" &&
        filtersRegistrationState?.sort?.order,
      filters: typeOfParticipationOptions, //@ts-ignore
      onFilter: (value: string, record) =>
        record?.typeOfParticipation === value,
      // ...getColumnSearchProps("typeOfParticipation"),
    },
    {
      title: "Pravno lice",
      width: 200,
      dataIndex: "companyName",
      key: "companyName",
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "companyName" &&
        filtersRegistrationState?.sort?.order,
      filteredValue: filtersRegistrationState?.filters?.companyName,
      sorter: (a, b) => {
        return (a?.companyName ?? "") < (b?.companyName ?? "") ? -1 : 1;
      },
      render: (companyName) => <span className="blue-text">{companyName}</span>,
      ...getColumnSearchProps("companyName"),
    },
    {
      title: "Datum prijave",
      dataIndex: "createdAt",
      key: "createdAt",
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "createdAt" &&
        filtersRegistrationState?.sort?.order,
      sorter: (a, b) =>
        new Date(a?.createdAt).getTime() < new Date(b?.createdAt).getTime()
          ? -1
          : 1,
      render: (createdAt) => {
        return (
          <span className="blue-text">
            {`${formatDate(new Date(createdAt))} ${formatTime(
              new Date(createdAt)
            )}h`}
          </span>
        );
      },
    },
    {
      title: "Tip učesnika",
      dataIndex: "type",
      key: "type",
      filteredValue: filtersRegistrationState?.filters?.type,
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "type" &&
        filtersRegistrationState?.sort?.order,
      sorter: (a, b) => ((a?.type ?? "") < (b?.type ?? "") ? -1 : 1),
      filters: typeOptions, //@ts-ignore
      onFilter: (value: string, record) => record?.type === value,
      render: (type) => <span className="blue-text">{type ?? "/"}</span>,
    },
    {
      title: "Tip smeštaja",
      dataIndex: "eventXRoomType",
      key: "eventXRoomType",
      filteredValue: filtersRegistrationState?.filters?.eventXRoomType,
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "eventXRoomType" &&
        filtersRegistrationState?.sort?.order,
      sorter: (a, b) =>
        (a?.eventXRoomType?.roomType?.name ?? "") <
        (b?.eventXRoomType?.roomType?.name ?? "")
          ? -1
          : 1,
      filters: roomTypeOptions, //@ts-ignore
      onFilter: (value: string, record) =>
        record?.eventXRoomType?.roomType?.name === value,
      render: (eventXRoomType) => (
        <span className="blue-text">
          {eventXRoomType?.roomType?.name ?? "/"}
        </span>
      ),
    },
    {
      title: "Tip sobe",
      dataIndex: "participant",
      key: "participant",
      filteredValue: filtersRegistrationState?.filters?.participant,
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "participant" &&
        filtersRegistrationState?.sort?.order,
      sorter: (a, b) =>
        (a?.participant?.roomKind?.name ?? "") <
        (b?.participant?.roomKind?.name ?? "")
          ? -1
          : 1,
      filters: roomTypeKindOptions, //@ts-ignore
      onFilter: (value: string, record) =>
        record?.participant?.roomKind?.name === value,
      render: (participant) => (
        <span className="blue-text">{participant?.roomKind?.name ?? "/"}</span>
      ),
    },
    {
      title: "Događaj",
      dataIndex: "event",
      key: "event",
      filteredValue: filtersRegistrationState?.filters?.event,
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "event" &&
        filtersRegistrationState?.sort?.order,
      sorter: (a, b) =>
        (a?.event?.name ?? "") < (b?.event?.name ?? "") ? -1 : 1,
      filters: allEventOptions, //@ts-ignore
      onFilter: (value: string, record) => record?.event?.name === value,
      render: (event) => <span className="blue-text">{event?.name}</span>,
    },
    {
      title: "Promena statusa",
      dataIndex: "lastStatusChange",
      key: "lastStatusChange",
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "lastStatusChange" &&
        filtersRegistrationState?.sort?.order,
      sorter: (a, b) =>
        new Date(a?.lastStatusChange).getTime() <
        new Date(b?.lastStatusChange).getTime()
          ? -1
          : 1,
      render: (date) => (
        <span className="blue-text">
          {`${formatDate(new Date(date))} ${formatTime(new Date(date))}h`}
        </span>
      ),
    },
    {
      title: "Profaktura",
      dataIndex: "invoice",
      key: "invoice",

      render: (invoice, record) => {
        return (
          <span className="blue-text">{record.invoice?.documentId || "/"}</span>
        );
      },
    },
    {
      title: "Tip kreiranja",
      dataIndex: "createdType",
      key: "createdType",
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "createdType" &&
        filtersRegistrationState?.sort?.order,
      filteredValue: filtersRegistrationState?.filters?.createdType,
      sorter: (a, b) =>
        (a?.createdType ?? "") < (b?.createdType ?? "") ? -1 : 1,
      render: (createdType) => (
        <span className="blue-text">{createdType ?? "/"}</span>
      ),
      ...getColumnSearchProps("createdType"),
    },
    {
      title: "Administrator",
      dataIndex: "userName",
      key: "userName",
      filteredValue: filtersRegistrationState?.filters?.userName,
      sorter: (a, b) => ((a?.userName ?? "") < (b?.userName ?? "") ? -1 : 1),
      defaultSortOrder:
        filtersRegistrationState?.sort?.columnKey == "userName" &&
        filtersRegistrationState?.sort?.order,
      render: (userName) => (
        <span className="blue-text">{userName ?? "/"}</span>
      ),
      ...getColumnSearchProps("userName"),
    },
    {
      title: "Opcije",
      key: "action",
      fixed: "right",
      width: 140,
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <span
            className="action-button"
            onClick={() => handleEditButton(record)}
          >
            {activeUser?.role?.name === RoleType.Agency
              ? "Pregled"
              : "Uredi / Dodaj"}
          </span>
        </Space>
      ),
    },
  ];

  const [copycolumns, setCopyColumns] = useState<
    ColumnsType<Registration | any>
  >([...columns]);

  /////// SETTING COLUMNS IN TABLE

  useEffect(() => {
    const filteredColumns = columns.filter((column: any) => {
      const activeColumn: any = listActiveColumns.find(
        (activeColumn: any) => activeColumn.text === column.title
      );

      return activeColumn ? activeColumn.active : true;
    });

    setCopyColumns(filteredColumns);
  }, [listActiveColumns, filtersRegistrationState]);

  useEffect(() => {
    const filteredColumns: any = columns
      .filter((column: any) => {
        const activeColumn: any = listActiveColumns.find(
          (activeColumn: any) => activeColumn.text === column.title
        );
        return activeColumn ? activeColumn.active : true;
      })
      .slice(1);

    const jsonListActiveColumnsA = localStorage.getItem(
      "activeColumnsRegistration"
    );

    let active = false;
    if (jsonListActiveColumnsA !== null) {
      const jsonArray = JSON.parse(jsonListActiveColumnsA);
      active = jsonArray[0].active;
    }

    if (active)
      setCopyColumns([
        {
          title: "Red. broj",
          width: 70,
          dataIndex: "firstName",
          key: "firstName",
          render: (firstName, text, index) => {
            return (
              <span className="blue-text">
                {(pageNumber - 1) * itemsPerPage + index + 1}
              </span>
            );
          },
        },
        ...filteredColumns,
      ]);
  }, [pageNumber]);

  const chengeSettingOptionFunc = (options: any) => {
    setListActiveColumns(options);

    localStorage.setItem("activeColumnsRegistration", JSON.stringify(options));
  };

  /////// END SETTING COLUMNS IN TABLE

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const handleEditButton = (row: any) => {
    if (row.registrationCompanyId !== null) {
      return navigate(
        `/edit-registration-company/${row.registrationCompanyId}`,
        {
          state: {
            edit: true,
          },
        }
      );
    } else {
      if (row.applicantId !== null) {
        return navigate(`/edit-registration/${row.applicantId}`, {
          state: {
            edit: true,
          },
        });
      } else {
        return navigate(`/edit-registration/${row.id}`, {
          state: {
            edit: true,
          },
        });
      }
    }
  };

  const handleChangeStatus = (newSelectedRegistration: Registration) => {
    setOpenStatusModal(true);
    setSelectedRegistration(newSelectedRegistration);
    setSelectedModalItemId(0);
  };

  const deleteRegistrationFunc = (selectedRegistration: Registration | any) => {
    setActiveSpinner(true);
    if (selectedRegistration !== undefined) {
      deleteRegistration(selectedRegistration?.id)
        .then(() => {
          setCancelledStatus(false);
          registrationData();
          handleCancel();
        })
        .finally(() => {
          setActiveSpinner(false);
        });
    }
  };

  const handleOk = () => {
    if (cancelledStatus) {
      setActiveSpinner(true);
      deleteRegistrationFunc(selectedRegistration);
    } else {
      setActiveSpinner(true);
      const copyOfNewRegistration: Registration = cloneDeep(
        selectedRegistration
      ) as Registration;
      const newParticipant: ParticipantDto = {} as ParticipantDto;
      if (newStatus === RegistrationStatus.Rejected) {
        copyOfNewRegistration.status = RegistrationStatus.Rejected;
        copyOfNewRegistration.lastStatusChange = new Date();

        updateRegistration(
          copyOfNewRegistration?.id as string, //@ts-ignore
          copyOfNewRegistration
        )
          .then(() => {
            let copyOfNewNotifications: Notification[] = [];
            getAllNotifications().then((response) => {
              response.data.result.map((item: any) => {
                if (
                  activeUser?.role.name === RoleType.Agency &&
                  item.type === NotificationType.AGENCY
                ) {
                  copyOfNewNotifications.push(item);
                }
                if (
                  activeUser?.role.name === RoleType.Employee &&
                  item.type === NotificationType.EMPLOYEE
                ) {
                  copyOfNewNotifications.push(item);
                }
                if (activeUser?.role.name === RoleType.Admin) {
                  copyOfNewNotifications.push(item);
                }
              });
              const unseenNotifications = copyOfNewNotifications.filter(
                (oneNotification: Notification) =>
                  oneNotification.seen === false
              );
              setNotificationNumber(unseenNotifications.length);
            });
          })
          .then(() => {
            toast?.current?.show({
              severity: "success",
              detail: "Uspešno ažuriran status prijave",
              life: 3000,
            });
            setOpenStatusModal(false);
          })
          .then(() => registrationData())
          .finally(() => setActiveSpinner(false))
          .catch((err) => console.log(err));
      }

      if (newStatus === RegistrationStatus.Approved) {
        setActiveSpinner(true);

        copyOfNewRegistration.status = RegistrationStatus.Approved;
        copyOfNewRegistration.lastStatusChange = new Date();

        // kreiranje ucesnika na osnovu prijave
        //@ts-ignore
        newParticipant.registration = { ...selectedRegistration };
        newParticipant.event = selectedRegistration?.event
          ? { ...selectedRegistration?.event }
          : {};
        newParticipant.firstName = selectedRegistration?.firstName as string;
        newParticipant.lastName = selectedRegistration?.lastName as string;
        newParticipant.country = selectedRegistration?.country as string;
        newParticipant.city = selectedRegistration?.city as string;
        newParticipant.street = selectedRegistration?.city as string;
        newParticipant.streetNumber =
          selectedRegistration?.streetNumber as string;
        if (selectedRegistration?.type) {
          newParticipant.type = selectedRegistration?.type;
        }
        newParticipant.email = selectedRegistration?.email as string;
        newParticipant.phone = selectedRegistration?.phone as string;
        newParticipant.personalId = selectedRegistration?.personalId;
        newParticipant.nationalId = selectedRegistration?.nationalId;
        newParticipant.qrCode = (selectedRegistration?.id +
          ";" +
          selectedRegistration?.phone +
          ";" +
          selectedRegistration?.firstName +
          ";" +
          selectedRegistration?.lastName +
          ";" +
          selectedRegistration?.event?.name) as string;
        newParticipant.note = selectedRegistration?.note;

        //create participant
        if (newParticipant && newParticipant.registration) {
          setOpenStatusModal(false);
          updateRegistration(
            copyOfNewRegistration?.id as string, //@ts-ignore
            copyOfNewRegistration
          )
            .then((res) => {
              let copyOfNewNotifications: Notification[] = [];
              getAllNotifications().then((response) => {
                response.data.result.map((item: any) => {
                  if (
                    activeUser?.role.name === RoleType.Agency &&
                    item.type === NotificationType.AGENCY
                  ) {
                    copyOfNewNotifications.push(item);
                  }
                  if (
                    activeUser?.role.name === RoleType.Employee &&
                    item.type === NotificationType.EMPLOYEE
                  ) {
                    copyOfNewNotifications.push(item);
                  }
                  if (activeUser?.role.name === RoleType.Admin) {
                    copyOfNewNotifications.push(item);
                  }
                });
                const unseenNotifications = copyOfNewNotifications.filter(
                  (oneNotification: Notification) =>
                    oneNotification.seen === false
                );
                setNotificationNumber(unseenNotifications.length);
              });

              //download qr code, convert to base64 image
              const canvas = document
                .getElementById(`myqrcode-${selectedRegistration?.id}`)
                ?.querySelector<HTMLCanvasElement>("canvas");
              let base64QrCode: string = "";

              if (canvas) {
                const borderedCanvas = document.createElement("canvas");
                borderedCanvas.width = canvas.width + 300;
                borderedCanvas.height = canvas.height + 300;
                const borderedCtx: any = borderedCanvas.getContext("2d");

                borderedCtx.fillStyle = "#ffffff";
                borderedCtx.fillRect(
                  0,
                  0,
                  borderedCanvas.width,
                  borderedCanvas.height
                );
                borderedCtx.drawImage(canvas, 150, 150);

                base64QrCode = borderedCanvas.toDataURL();
              }

              newParticipant.registration = res.data;
              createParticipant(newParticipant, base64QrCode);
            })
            .then(() => {
              toast?.current?.show({
                severity: "success",
                detail: "Uspešno ažuriran status prijave",
                life: 3000,
              });
            })
            .then()
            .finally(() => {
              registrationData();
            })
            .catch((err) => {
              let text = err.response.data.message;
              toast?.current?.show({
                severity: "error",
                detail: `${text}`,
                life: 3000,
              });
              setOpenStatusModal(false);
            });
          ///
        }
      }

      if (newStatus === RegistrationStatus.Paid) {
        copyOfNewRegistration.status = RegistrationStatus.Paid;
        copyOfNewRegistration.lastStatusChange = new Date();
        payRegistration(copyOfNewRegistration.id as string)
          .then(() => {
            toast?.current?.show({
              severity: "success",
              detail: "Uspešno ažuriran status prijave",
              life: 3000,
            });
            setOpenStatusModal(false);
          })
          .then(() => registrationData())
          .finally(() => setActiveSpinner(false))
          .catch((err) => console.log(err));
      }
    }
  };

  const handleCancel = () => {
    setOpenStatusModal(false);
    setNewStatus(RegistrationStatus.Pending);
    setCancelledStatus(false);
  };

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getRegistrationListReportTemplate(filteredRegistrationList);
    setPdfData(newPdf);
  }, [filteredRegistrationList]);

  const handleOpenExportButtons = () => {
    setOpenExportButtons(!openExportButtons);
  };

  const handleExcelButton = () => {
    const filterObject = filteredRegistrationList.map((el: any) => {
      return {
        Ime: el?.firstName,
        Prezime: el?.lastName,
        Dogadjaj: el?.event?.name,
        Od: el?.date ? formatDate(new Date(el?.date[0])) : "",
        Do: el?.date ? formatDate(new Date(el?.date[1])) : "",
        Broj_dana: el?.numberOfDays ?? "",
        Tip_sobe: el?.eventXRoomType?.roomType?.name ?? "",
        Cena: el?.priceOfAccommodation ?? "",
        O_trosku_Uos: el?.uosPayment === true ? "Da" : "Ne",
        Tip: el?.type ?? "",
        Kompanija: el?.companyName ?? "/",
        JMBG: el?.personalId ?? "",
        BrLk_Pasosa: el?.nationalId ?? "",
        Adresa: el?.street ?? "",
        Kucni_broj: el?.streetNumber ?? "",
        Grad: el?.city ?? "",
        Zemlja: el?.country ?? "",
        Telefon: el?.phone ?? "",
        Email: el?.email ?? "",
        Datum_prijave: formatDate(new Date(el?.createdAt)),
        Status: el?.status,
        Promena_statusa: formatDate(new Date(el?.lastStatusChange)),
        Tip_kreiranja: el?.createdType,
        Administrator: el?.userName,
        Napomena: el?.note ?? "",
      };
    });

    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(filterObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      saveAsExcelFile(excelBuffer, "Lista prijava");
    });
    setOpenExportButtons(false);
  };

  const saveAsExcelFile = (buffer: any, fileName: string) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(
          data,
          fileName +
            "_" +
            formatDate(new Date()) +
            "_" +
            formatTime(new Date()) +
            EXCEL_EXTENSION
        );
      }
    });
  };

  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowMessage(false);
      if (selectedRegistration?.registrationCompanyId) {
        const res = await getCompanyRegistration(
          selectedRegistration.registrationCompanyId
        );
        const data = res.data.result;

        if (data.accommodations && data.accommodations.length === 1) {
          setShowMessage(true);
        }
      }
    };

    fetchData();
  }, [selectedRegistration]);

  const [showMessageRegistration, setShowMessageRegistration] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowMessageRegistration(false);
      if (
        selectedRegistration &&
        selectedRegistration?.registrationCompanyId == null
      ) {
        let currentId: any =
          selectedRegistration?.applicantId == null
            ? selectedRegistration?.id
            : selectedRegistration?.applicantId;

        const res = await getRegistration(currentId);
        const data = res.data.result;

        if (data.accommodations && data.accommodations.length === 1) {
          setShowMessageRegistration(true);
        }
      }
    };

    fetchData();
  }, [selectedRegistration]);

  const logicWidthFunc = () => {
    const listActiveColumnsLength = listActiveColumns.filter(
      (el) => el.active === true
    ).length;

    switch (true) {
      case listActiveColumnsLength <= 9:
        return 1500;
      case listActiveColumnsLength < 12:
        return 1800;
      case listActiveColumnsLength <= 14:
        return 2000;
      default:
        return 2500;
    }
  };

  return (
    <PageLayout>
      <Spin spinning={activeSpinner}>
        <Toast ref={toast} position={"top-right"} />
        <div className="registration-list">
          <div className="overview-legend-left">
            <span className="legend-row" style={{ marginRight: "15px" }}>
              <CustomButton
                danger={false}
                htmlType={"submit"}
                key={2}
                type={"primary"}
                function={() => {
                  // setResetData(!resetData);
                  // dispatch(resetFiltersRegistration());
                  window.location.reload();
                }}
                text={"Resetuj sve filtere"}
              />
            </span>
            <p className="legend-text">Statusi:</p>
            <div className="legend-row">
              <ProfileOutlined className="legend-icon-paid" />
              <p className="legend-icon-paid-text">Uplaćeno</p>
            </div>
            <div className="legend-row">
              <ProfileOutlined className="legend-icon-reserved" />
              <p className="legend-icon-reserved-text">Rezervisano</p>
            </div>
            <div className="legend-row">
              <ProfileOutlined className="legend-icon-approved" />
              <p className="legend-icon-approved-text">Odobreno</p>
            </div>
            <div className="legend-row">
              <ProfileOutlined className="legend-icon-rejected" />
              <p className="legend-icon-rejected-text">Odbijeno</p>
            </div>
            <div className="legend-row">
              <ProfileOutlined className="legend-icon-pending" />
              <p className="legend-icon-pending-text">Na čekanju</p>
            </div>
          </div>

          <div className="settings-columns">
            ,
            <SettingsColumns
              listActiveColumns={listActiveColumns}
              chengeSettingOptionFunc={chengeSettingOptionFunc}
            />
          </div>

          <Table
            columns={copycolumns}
            sticky={true}
            scroll={{
              x: logicWidthFunc(),
            }}
            rowKey={(data) => {
              return data.id;
            }}
            pagination={{
              current: currentPage,
              onChange(current) {
                setPageNumber(current);
                dispatch(setCurrentPage(current)); /// DISPATCH ACTION FROM REDUX
              },
              pageSize: itemsPerPage,
              showSizeChanger: true,
              pageSizeOptions: ["30", "50", "100"],
              onShowSizeChange(current, pageSize) {
                dispatch(setPageItems(pageSize));
              },
            }}
            dataSource={registrationList}
            onChange={(pagination, filters, sorter, extra) => {
              // setPaginationNumber(pagination.pageSize);
              setFilteredRegistrationList(extra.currentDataSource);
              dispatch(
                setFiltersRegistration({
                  filters: filters,
                  sort: sorter,
                })
              );
            }}
            sortDirections={["ascend", "descend"]}
            className="table-container"
          />
          <div className="footer-container">
            <div className="btn-container footer-btn-container">
              {/* <Button
                type="primary"
                onClick={handleEmailButton}
                className="filter-button"
                size="small"
              >
                E-mail obaveštenje
              </Button> */}
              <CustomButton
                danger={false}
                htmlType={"submit"}
                key={1}
                type={"primary"}
                function={() => {
                  handleOpenExportButtons();
                }}
                text={"Export"}
              />
            </div>
          </div>
          {openExportButtons ? (
            <div className="btn-container footer-btn-container">
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => {
                  handleExcelButton();
                }}
                text={"Excel"}
              />
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                }}
                text={"PDF"}
              />
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                }}
                text={"Štampa"}
              />
            </div>
          ) : null}
          <div className="document-preview">
            <PdfPreview
              pdfDefinition={pdfData}
              downloadDocument={downloadPdfDocument}
              printDocument={printPdfDocument}
              documentName={`Lista_prijava_${
                formatDate(new Date()) + "_" + formatTime(new Date())
              }.pdf`}
            />
          </div>
          <Modal
            maskClosable={false}
            open={openStatusModal}
            title="Promena statusa prijave"
            onOk={handleOk}
            onCancel={handleCancel}
            footer={[
              <CustomButton
                danger={false}
                htmlType={"submit"}
                key={"back"}
                type={"primary"}
                function={() => {
                  handleCancel();
                }}
                text={"Otkaži"}
              />,
              <CustomButton
                danger={false}
                htmlType={"submit"}
                key={"back"}
                type={"primary"}
                function={() => {
                  handleOk();
                }}
                text={"Potvrdi"}
              />,
            ]}
          >
            <div className="body-modal-custom">
              {selectedRegistration?.status === RegistrationStatus.Pending && (
                <div className="blue-text-modal" style={{ display: "inline" }}>
                  <b style={{ display: "inline" }}>
                    {`${selectedRegistration?.firstName} ${selectedRegistration?.lastName}`}{" "}
                  </b>
                  će biti Odobreno/Odbijeno učešće na događaju.
                </div>
              )}

              {(selectedRegistration?.status === RegistrationStatus.Rejected ||
                selectedRegistration?.status === RegistrationStatus.Paid ||
                selectedRegistration?.status === RegistrationStatus.Reserved ||
                selectedRegistration?.status ===
                  RegistrationStatus.Approved) && (
                <div className="blue-text-modal" style={{ display: "inline" }}>
                  Ukoliko otkažete prijavu učesnik{" "}
                  <b style={{ display: "inline" }}>
                    {`${selectedRegistration?.firstName} ${selectedRegistration?.lastName}`}{" "}
                    {selectedRegistration?.eventXRoomType?.roomType?.capacity >
                      1 && "i ostali učesnici koji se nalaze u sobi"}
                  </b>{" "}
                  će biti{" "}
                  {selectedRegistration?.eventXRoomType?.roomType?.capacity > 1
                    ? "obrisani"
                    : "obrisan"}{" "}
                  i smeštaj oslobođen.
                  {showMessage && (
                    <b className="blue-text-info">
                      {" "}
                      Ovo je zadnja osoba u prijavi, ako nju izbrišete
                      automatski će biti izbrisana i profaktura.
                    </b>
                  )}
                  {showMessageRegistration && (
                    <b>
                      Ako izbrišete ovu prijavu, automatski će biti izbrisana i
                      profaktura.
                    </b>
                  )}
                </div>
              )}
              <div className="button-section">
                {selectedRegistration?.status ===
                  RegistrationStatus.Pending && (
                  <div
                    className={"status-item"}
                    onClick={() => {
                      setNewStatus(RegistrationStatus.Approved);
                      setSelectedModalItemId(2);
                    }}
                    style={
                      selectedModalItemId === 2
                        ? { background: "#0D4077", border: "1px solid #0D4077" }
                        : {
                            background: "white",
                            color: "#0D4077",
                            border: "1px solid #0D4077",
                          }
                    }
                  >
                    Odobri
                  </div>
                )}
                {selectedRegistration?.status ===
                  RegistrationStatus.Pending && (
                  <div
                    className={"status-item"}
                    onClick={() => {
                      setNewStatus(RegistrationStatus.Rejected);
                      setSelectedModalItemId(1);
                    }}
                    style={
                      selectedModalItemId === 1
                        ? { background: "#C7363D", border: "1px solid #C7363D" }
                        : {
                            background: "white",
                            color: "#C7363D",
                            border: "1px solid #C7363D",
                          }
                    }
                  >
                    Odbij
                  </div>
                )}

                {selectedRegistration?.status ===
                  RegistrationStatus.Reserved && (
                  <p
                    className={"status-item"}
                    onClick={() => {
                      setNewStatus(RegistrationStatus.Paid);
                      setSelectedModalItemId(1);
                    }}
                    style={
                      selectedModalItemId === 1
                        ? { background: "#52C41A", border: "1px solid #52C41A" }
                        : {
                            background: "white",
                            color: "#52C41A",
                            border: "1px solid #52C41A",
                          }
                    }
                  >
                    Plaćeno
                  </p>
                )}

                {selectedRegistration?.status !==
                  RegistrationStatus.Pending && (
                  <div
                    className={"status-item"}
                    onClick={() => {
                      setCancelledStatus(true);
                      setSelectedModalItemId(3);
                      //deleteRegistrationFunc(selectedRegistration);
                      //handleCancel();
                    }}
                    style={
                      selectedModalItemId === 3
                        ? {
                            background: "#DCE6F5",
                            color: "#002F6C",
                            border: "1px solid #DCE6F5",
                          }
                        : {
                            background: "white",
                            color: "#002F6C",
                            border: "1px solid #002F6C",
                          }
                    }
                  >
                    Otkazano
                  </div>
                )}
              </div>
            </div>
          </Modal>
          <Modal
            maskClosable={false}
            open={showCommentModal}
            title={`Novi komentar za ${regsitrationComment}`}
            onCancel={() => setShowCommentModal(false)}
            onOk={() => setShowCommentModal(false)}
            footer={[
              <CustomButton
                danger={false}
                htmlType={"submit"}
                key={1}
                type={"primary"}
                function={() => {
                  postNewComment();
                  setShowCommentModal(false);
                }}
                text={"Potvrdi"}
              />,
              <CustomButton
                danger={false}
                htmlType={"submit"}
                key={2}
                type={"primary"}
                function={() => {
                  setCommentText("");
                  setShowCommentModal(false);
                }}
                text={"Zatvori"}
              />,
            ]}
          >
            <div style={{ padding: "20px 0px" }}>
              <TextArea
                value={commentText}
                onChange={(e) => handleComment(e)}
                rows={4}
                placeholder="Unesite komentar"
              />
            </div>
          </Modal>
        </div>
      </Spin>
    </PageLayout>
  );
};

export default RegistrationListPage;
